import useThemes from 'hooks/useThemes';
import { ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import Typo from 'consts/typography';
import { isValidAmount } from './WithdrawModal';
import { CoinMarketPriceResponseModel, CoinPairDto } from 'models/wallet';
import walletApi from 'api/walletApi';
import transactionApi from 'api/transactionApi';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import ArrowIcon from 'assets/icons/ArrowIcon';
import { Select } from 'antd';
import { IMG_ALT } from 'consts/app';
import BigNumber from 'bignumber.js';
import { formatAmount, formatAmountV2 } from 'utils/formatAmount';
interface BuyModalProps {}

interface UsdChangeType {
  amount: string;
  checked: boolean;
}

const BuyModal: React.FunctionComponent<BuyModalProps> = (props) => {
  const { currentTheme } = useThemes();
  const [amount, setAmount] = useState<string>('');
  const [usdChange, setUsdChange] = useState<UsdChangeType>({
    amount: '',
    checked: false,
  });

  const { isPhone, isTablet } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const et = useErrTranslation();
  const { userWallets } = useHandleGetWallets();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [coinPairs, setCoinPairs] = useState<CoinPairDto[] | null>(null);
  const [currentCoinPair, setCurrentCoinPair] = useState<CoinPairDto>();

  const [prices, setPrices] = useState<CoinMarketPriceResponseModel[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const getCoinPairs = async () => {
    const { ok, body } = await walletApi.getCoinPair();
    if (ok && body) {
      setCoinPairs(body);
    }
  };

  const getPrice = async () => {
    const { ok, body } = await walletApi.getTokenPrices();
    if (ok && body) {
      setPrices(body);
    }
  };

  let idInterval: any = -1;

  useEffect(() => {
    getCoinPairs();
    getPrice();

    idInterval = setInterval(() => {
      getPrice();
    }, 5000);

    return () => {
      if (idInterval !== -1) clearInterval(idInterval);
    };
  }, []);

  const rate = useMemo(() => {
    const quotePrice = prices.find((price) => price.coin.id === currentCoinPair?.quote.id)?.price;
    const basePrice = prices.find((price) => price.coin.id === currentCoinPair?.base.id)?.price;
    if (quotePrice && basePrice) {
      return new BigNumber(quotePrice).div(basePrice).toNumber();
    }
    return 1;
  }, [prices, currentCoinPair?.id]);

  const getFeeSwap = useMemo(() => {
    if (currentCoinPair?.isFixed === true) {
      return currentCoinPair.fixedFee;
    }
    if (currentCoinPair?.isPercentage === true) {
      return new BigNumber(Number(amount))
        .multipliedBy(rate)
        .multipliedBy(currentCoinPair.percentageFee / 100)
        .toNumber();
    } else {
      return 0;
    }
  }, [coinPairs, currentCoinPair?.id, amount, rate]);

  const receiveAmount = useMemo(() => {
    if (usdChange.amount === '' && usdChange.checked) {
      return '0.0';
    }
    if (rate && amount) {
      const result =
        new BigNumber(Number(amount.replace(',', ''))).multipliedBy(rate).toNumber() - getFeeSwap;
      return result.toString();
    }

    return '0.0';
  }, [amount, rate, usdChange]);

  useEffect(() => {
    if (usdChange.amount === '' && usdChange.checked) {
      setErrorMessage('');
    } else if (amount !== '' && Number(formatAmountV2(receiveAmount, 8)) <= 0) {
      setErrorMessage(t('tbet.swap.error.receiveAmount'));
    } else {
      setErrorMessage('');
    }
  }, [receiveAmount, usdChange, amount]);

  const resetModal = () => {
    setAmount('');
    setUsdChange({
      amount: '',
      checked: false,
    });
  };

  const selectionFromCoin = useMemo(() => {
    if (coinPairs) {
      const sortedCoinPairs = coinPairs.sort((a, b) => a.displayOrder - b.displayOrder);
      setCurrentCoinPair(sortedCoinPairs[0]);
      return sortedCoinPairs.map((p) => ({
        value: p.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={p.quote.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {p.quote.symbol}
            </span>
          </div>
        ),
        // symbol: p.quote.symbol,
        // minAmount: p.quote.minAmount,
        // maxAmount: p.quote.maxAmount,
      }));
    }
  }, [coinPairs]);

  const selectionToCoin = useMemo(() => {
    if (coinPairs) {
      const sortedCoinPairs = coinPairs.sort((a, b) => a.displayOrder - b.displayOrder);
      return sortedCoinPairs.map((p) => ({
        value: p.id,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={p.base.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {p.base.symbol}
            </span>
          </div>
        ),
        // symbol: p.quote.symbol,
        // minAmount: p.quote.minAmount,
        // maxAmount: p.quote.maxAmount,
      }));
    }
  }, [coinPairs]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,8})?|0\.)$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const onUsdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,2})?|0\.)$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setUsdChange((prev) => ({ ...prev, amount: value }));
    }
  };

  const onSubmit = useCallback(async () => {
    if (currentCoinPair?.id) {
      setIsLoading(true);
      const { ok, body, error } = await transactionApi.buyUsdAndTbet({
        amount: parseFloat(amount),
        coinPairId: currentCoinPair?.id,
      });
      if (ok && body) {
        const onOk = () => {
          resetModal();
        };
        showSuccessModal({
          title: t('tbet.buy.success-title'),
          content: t('tbet.buy.success-message', {
            amount: `${receiveAmount} ${currentCoinPair.base.symbol}`,
          }),
          onOk,
        });
      }
      if (error) {
        const onOk = () => {};
        showErrorModal({
          content: et(error.message),
          onOk,
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
      }
      setIsLoading(false);
    }
  }, [amount]);

  const onCoinQuoteChange = (event: number) => {
    setCurrentCoinPair(coinPairs?.find((c) => c.id === event));
  };

  const onUsdCheck = (event: ChangeEvent<HTMLInputElement>) => {
    setUsdChange((prev) => ({ ...prev, checked: !prev.checked }));
  };

  useEffect(() => {
    const usdRate = prices.find((price) => price.coin.id === currentCoinPair?.quote.id)?.price;
    if (usdRate && usdChange.checked) {
      const actualAmount = new BigNumber(Number(usdChange.amount)).div(usdRate).toString();
      setAmount(formatAmount(actualAmount, 8).replace(',', ''));
    }
  }, [usdChange, prices, currentCoinPair?.id]);

  return (
    <>
      {coinPairs && (
        <div className={`flex w-100 items-center swap-token__container `}>
          <div className="swap-token__coin">
            <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.from')}</span>
            <div>
              <Select
                className="antd__select w-full"
                dropdownClassName="antd__select-dropdown"
                value={currentCoinPair?.id}
                onChange={onCoinQuoteChange}
                options={selectionFromCoin}
              />
            </div>
            <span className={`primary-text-color ${isPhone ? Typo.p9 : Typo.p5} `}>
              {t('balance')}:{' '}
              {formatAmountV2(
                userWallets.find((w) => w.coin.id === currentCoinPair?.quote.id)?.balance,
                8,
              )}
            </span>
          </div>
          <ArrowIcon
            fill={currentTheme === 'dark' ? '#FFFFFF' : '#000000'}
            width="40px"
            height="40px"
          />
          <div className="swap-token__coin">
            <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.to')}</span>
            <div>
              <Select
                className="antd__select w-full"
                dropdownClassName="antd__select-dropdown"
                value={currentCoinPair?.id}
                options={selectionToCoin}
                disabled={true}
                suffixIcon={null}
              />
            </div>
            <span className={`primary-text-color ${isPhone ? Typo.p9 : Typo.p5} `}>
              {t('balance')}:{' '}
              {formatAmountV2(
                userWallets.find((w) => w.coin.id === currentCoinPair?.base.id)?.balance,
                8,
              )}
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3 items-center">
        <Fragment>
          <div className=" w-100 usd-checkbox swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              USD
            </label>
            <div className="flex ">
              <input
                type="checkbox"
                onChange={onUsdCheck}
                checked={usdChange.checked}
                style={{ accentColor: 'var(--textColorButtonMainTabHover)', zoom: '1.3' }}
              />
            </div>
          </div>
          {usdChange.checked ? (
            <>
              <div className="common-input input-amount w-100 swap-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                >
                  {t('cashier.amount')}
                </label>
                <div className="usd-to-amount-input">
                  <div className="flex-row items-center">
                    <span>$</span>
                    <input
                      type="text"
                      name="amount"
                      id="amount"
                      autoComplete="off"
                      onChange={onUsdChange}
                      value={usdChange.amount}
                      placeholder={t('placeholder.enterAmount')}
                    />
                  </div>

                  <p>{`${currentCoinPair?.quote.symbol} ${formatAmountV2(amount, 8)}`}</p>
                </div>
              </div>
              {/* <div className="common-input input-amount w-100 swap-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                ></label>
                <CustomAmountButton
                  symbol="USD"
                  onSetField={handleSetField}
                  onOptionDoubleClick={handleDoubleClickOption}
                  classes="custom-button-swap"
                />
              </div> */}
            </>
          ) : (
            <>
              <div className="common-input input-amount w-100 swap-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                >
                  {t('cashier.amount')}
                </label>

                <input
                  type="text"
                  name="amount"
                  id="amount"
                  autoComplete="off"
                  onChange={onChange}
                  value={amount}
                  placeholder={t('placeholder.enterAmount')}
                />
              </div>
              {/* <div className="common-input input-amount w-100 swap-input">
                <label
                  className={`${
                    isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
                  } font-medium primary-text-color`}
                  htmlFor="amount"
                ></label>
                <CustomAmountButton
                  symbol={currentSymbol}
                  onSetField={handleSetField}
                  onOptionDoubleClick={handleDoubleClickOption}
                  classes="custom-button-swap"
                />
              </div> */}
            </>
          )}
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('tbet.buy.rate')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              disabled={true}
              value={formatAmountV2(rate, 8)}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="feeswap"
            >
              {t('tbet.buy.feeSwap')}
            </label>
            <input
              type="text"
              name="feeswap"
              id="feeswap"
              autoComplete="off"
              disabled={true}
              value={formatAmountV2(getFeeSwap, 8)}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('tbet.buy.receiveAmount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              disabled={true}
              value={formatAmountV2(receiveAmount, 8)}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
            ></label>
            <span className="error-message">{errorMessage}</span>
          </div>
        </Fragment>
        <div className="withdraw-balance swap-input">
          <div className="withdraw-balance__left"></div>
          <div className="withdraw-balance__right">
            <button
              disabled={
                isLoading || Number(amount) === 0 || Number(formatAmountV2(receiveAmount, 8)) <= 0
              }
              className={`button-generate ${
                isLoading || Number(amount) === 0 || Number(formatAmountV2(receiveAmount, 8)) <= 0
                  ? 'disabled'
                  : ''
              }`}
              type="submit"
              onClick={onSubmit}
            >
              {t('tbet.buy.button')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BuyModal;
